/**
 * Settings → Compliance.
 *
 * Catalog view for requirements (HR defines them) + an at-a-glance
 * summary of org-wide completion. Per-employee records are surfaced on
 * the employee profile, not here.
 */
import { useEffect, useState } from "react";
import { Pencil, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  createRequirement,
  deleteRequirement,
  getComplianceSummary,
  listRequirements,
  updateRequirement,
  type ComplianceCategory,
  type ComplianceRequirement,
  type ComplianceSummary,
} from "@/api/compliance";

interface ReqForm {
  name: string;
  description: string;
  category: ComplianceCategory;
  required_for_all: boolean;
  required_for_titles: string; // comma-separated in the UI
  renewal_interval: "" | "annual" | "biannual" | "custom";
  renewal_days: string;
  grace_period_days: string;
}

const EMPTY: ReqForm = {
  name: "",
  description: "",
  category: "training",
  required_for_all: true,
  required_for_titles: "",
  renewal_interval: "",
  renewal_days: "",
  grace_period_days: "30",
};

export function ComplianceSettings() {
  const { addToast } = useToast();
  const [reqs, setReqs] = useState<ComplianceRequirement[]>([]);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ComplianceRequirement | null>(null);
  const [form, setForm] = useState<ReqForm>(EMPTY);

  const load = async () => {
    try {
      const [rs, s] = await Promise.all([
        listRequirements(),
        getComplianceSummary(),
      ]);
      setReqs(rs);
      setSummary(s);
    } catch {
      addToast("error", "Failed to load compliance data");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (r: ComplianceRequirement) => {
    setEditing(r);
    setForm({
      name: r.name,
      description: r.description ?? "",
      category: r.category,
      required_for_all: r.required_for_all,
      required_for_titles: (r.required_for_titles ?? []).join(", "),
      renewal_interval:
        (r.renewal_interval as ReqForm["renewal_interval"]) ?? "",
      renewal_days: r.renewal_days != null ? String(r.renewal_days) : "",
      grace_period_days: String(r.grace_period_days ?? 30),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      addToast("error", "Name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      category: form.category,
      required_for_all: form.required_for_all,
      required_for_departments: null,
      required_for_titles: form.required_for_titles
        ? form.required_for_titles
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : null,
      renewal_interval: form.renewal_interval || null,
      renewal_days: form.renewal_days ? Number(form.renewal_days) : null,
      grace_period_days: Number(form.grace_period_days || 30),
    };
    try {
      if (editing) {
        await updateRequirement(editing.id, payload as Partial<ComplianceRequirement>);
      } else {
        await createRequirement(payload as never);
      }
      setOpen(false);
      addToast("success", "Requirement saved");
      await load();
    } catch {
      addToast("error", "Failed to save");
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteRequirement(id);
      addToast("success", "Requirement archived");
      await load();
    } catch {
      addToast("error", "Failed to archive");
    }
  };

  return (
    <div className="space-y-6">
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Compliance overview</CardTitle>
          </CardHeader>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat label="Total" value={summary.total} />
            <Stat label="Completed" value={summary.completed} tone="success" />
            <Stat label="Pending" value={summary.pending} />
            <Stat
              label="Expiring soon"
              value={summary.expiring_soon}
              tone="warning"
            />
            <Stat label="Expired" value={summary.expired} tone="danger" />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-text-muted mb-1">
              <span>Org-wide completion</span>
              <span>{summary.percent_complete}%</span>
            </div>
            <div className="h-2 rounded-full bg-surface overflow-hidden">
              <div
                className="h-full bg-accent"
                style={{ width: `${summary.percent_complete}%` }}
              />
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Requirements</CardTitle>
          <Button size="sm" onClick={openNew}>
            <Plus className="h-4 w-4" />
            New requirement
          </Button>
        </CardHeader>
        {reqs.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-muted">
            <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No requirements defined yet.
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {reqs.map((r) => (
              <div
                key={r.id}
                className="py-3 flex items-center gap-3 justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-text-primary truncate">
                      {r.name}
                    </p>
                    <Badge variant="default">{r.category}</Badge>
                    {r.required_for_all ? (
                      <Badge variant="info">All employees</Badge>
                    ) : (
                      <Badge variant="default">Scoped</Badge>
                    )}
                    {r.renewal_interval && (
                      <Badge variant="default">
                        Renews {r.renewal_interval}
                      </Badge>
                    )}
                    {!r.is_active && (
                      <Badge variant="default">Archived</Badge>
                    )}
                  </div>
                  {r.description && (
                    <p className="text-xs text-text-muted mt-0.5 truncate">
                      {r.description}
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {r.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(r.id)}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit requirement" : "New requirement"}
        size="lg"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sexual Harassment Prevention Training"
            />
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Category
              </label>
              <Select
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value as ComplianceCategory,
                  })
                }
                options={[
                  { value: "certification", label: "Certification" },
                  { value: "training", label: "Training" },
                  { value: "license", label: "License" },
                  { value: "clearance", label: "Clearance" },
                ]}
              />
            </div>
          </div>
          <Input
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
          />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Renewal
              </label>
              <Select
                value={form.renewal_interval}
                onChange={(e) =>
                  setForm({
                    ...form,
                    renewal_interval:
                      e.target.value as ReqForm["renewal_interval"],
                  })
                }
                options={[
                  { value: "", label: "One-time" },
                  { value: "annual", label: "Annual" },
                  { value: "biannual", label: "Every 2 years" },
                  { value: "custom", label: "Custom (days)" },
                ]}
              />
            </div>
            {form.renewal_interval === "custom" && (
              <Input
                label="Renewal days"
                type="number"
                value={form.renewal_days}
                onChange={(e) =>
                  setForm({ ...form, renewal_days: e.target.value })
                }
              />
            )}
            <Input
              label="Grace period (days)"
              type="number"
              value={form.grace_period_days}
              onChange={(e) =>
                setForm({ ...form, grace_period_days: e.target.value })
              }
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-border text-accent"
              checked={form.required_for_all}
              onChange={(e) =>
                setForm({ ...form, required_for_all: e.target.checked })
              }
            />
            Required for all employees
          </label>
          {!form.required_for_all && (
            <Input
              label="Required for titles (comma-separated, use * suffix for prefix match)"
              value={form.required_for_titles}
              onChange={(e) =>
                setForm({ ...form, required_for_titles: e.target.value })
              }
              placeholder="Driver*, Nurse*, Safety Officer"
            />
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save}>
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "success" | "warning" | "danger";
}) {
  const color =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "danger"
          ? "text-danger"
          : "text-text-primary";
  return (
    <div className="rounded-lg border border-border/40 p-3">
      <div className="text-xs text-text-muted">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
